// 📄 components/StepLogicalSystem.tsx
import React, { useState } from 'react';
import { Plus, List, Trash2, Edit3 } from 'lucide-react';
import { GeneralsFormData } from '../../../../../services/commerce/integrationService.tsx';

interface StepLogicalSystemProps {
  realmId: string;
  apiClientId: string;
  onChange: (field: keyof GeneralsFormData, value: any) => void;
  isInvalid: boolean;
}

interface SapLogicalSystemRow {
  logSystem: string;
  name: string;
}

export function StepLogicalSystem({ realmId, apiClientId, onChange, isInvalid }: StepLogicalSystemProps) {
  // 📝 Datos simulados de la tabla SAP BD54
  const [systems, setSystems] = useState<SapLogicalSystemRow[]>([
    { logSystem: 'ARIBACIG', name: 'ARIBA Logical System' },
    { logSystem: 'C10EWM100', name: 'a' },
    { logSystem: 'C10EWM110', name: 'a' },
    { logSystem: 'C10EWM120', name: 'a' },
  ]);

  // Estados para simular la inserción de una nueva fila ("New Entries")
  const [newLogSystem, setNewLogSystem] = useState('ARIBACIG');
  const [newName, setNewName] = useState('ARIBA Logical System');
  const [isAdding, setIsAdding] = useState(false);

  const handleAddRow = () => {
    if (!newLogSystem.trim()) return;
    // Si ya existe uno con el mismo nombre de sistema, lo filtramos para simular un "update/insert"
    const filtered = systems.filter(sys => sys.logSystem !== newLogSystem.toUpperCase());
    setSystems([
      { logSystem: newLogSystem.toUpperCase(), name: newName },
      ...filtered
    ]);
    setIsAdding(false);
  };

  return (
    <div className="space-y-4 animate-fadeIn">
      {/* Guía de Ruta SPRO de SAP */}
      <div className="bg-blue-500/5 border border-blue-500/10 rounded-xl p-3 mb-2 space-y-1">
        <span className="text-[10px] font-bold text-blue-400 uppercase tracking-widest block">Ruta SPRO / Transacción SAP</span>
        <p className="font-mono text-xs text-gray-300">
          <span className="bg-zinc-800 px-1.5 py-0.5 rounded text-gray-400 font-bold mr-1.5">BD54</span> 
          <span>SPRO → Integration with Other SAP Components → SAP Ariba Cloud Integration Gateway → Global Settings → Create Logical System</span>
        </p>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
        <div>
          <h3 className="text-lg font-medium text-blue-400">4. Logical System (Sistema Lógico)</h3>
          <p className="text-sm text-gray-400">
            Define el sistema lógico que identificará el entorno del ERP para la comunicación en OneHub Cloud.
          </p>
        </div>
      </div>

      {/* 🖥️ CLON DE LA INTERFAZ SAP "Change View 'Logical Systems': Overview" */}
      <div className="border border-white/10 rounded-xl overflow-hidden bg-zinc-900/40">
        
        {/* Barra de Herramientas superior de SAP (New Entries, etc.) */}
        <div className="flex items-center justify-between bg-zinc-800/80 border-b border-white/10 p-2">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setIsAdding(!isAdding)}
              className={`flex items-center gap-1 px-3 py-1 rounded text-xs font-medium border transition-all ${
                isAdding 
                  ? 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' 
                  : 'bg-zinc-700 text-white border-white/5 hover:bg-zinc-600'
              }`}
            >
              <Plus className="w-3.5 h-3.5 text-green-400" /> New Entries
            </button>
            <span className="text-xs font-mono text-gray-500 hidden sm:inline">Table View Maintenance</span>
          </div>
          <span className="text-[11px] font-mono font-bold text-blue-400 bg-blue-500/5 px-2 py-0.5 rounded border border-blue-500/10">
            Maint. View: V_TBDLS
          </span>
        </div>

        {/* Formulario rápido para simular "New Entries" */}
        {isAdding && (
          <div className="p-3 bg-yellow-500/5 border-b border-white/10 grid grid-cols-1 sm:grid-cols-3 gap-3 items-end animate-fadeIn">
            <div>
              <label className="block text-[10px] text-yellow-400 font-medium mb-0.5">Log. System</label>
              <input 
                type="text" 
                value={newLogSystem} 
                onChange={(e) => setNewLogSystem(e.target.value.toUpperCase())}
                placeholder="ej. ARIBACIG"
                className="w-full bg-zinc-800 border border-white/10 rounded px-2 py-1 text-xs font-mono uppercase text-white focus:outline-none focus:border-yellow-500"
              />
            </div>
            <div>
              <label className="block text-[10px] text-yellow-400 font-medium mb-0.5">Name</label>
              <input 
                type="text" 
                value={newName} 
                onChange={(e) => setNewName(e.target.value)}
                placeholder="ej. ARIBA Logical System"
                className="w-full bg-zinc-800 border border-white/10 rounded px-2 py-1 text-xs text-white focus:outline-none focus:border-yellow-500"
              />
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={handleAddRow} className="bg-green-600 hover:bg-green-700 text-[#fff] text-xs px-3 py-1 rounded font-medium flex-1">
                Insertar
              </button>
              <button type="button" onClick={() => setIsAdding(false)} className="bg-zinc-700 hover:bg-zinc-600 text-gray-300 text-xs px-2 py-1 rounded">
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Tabla Interactiva de Sistemas Lógicos */}
        <div className="max-h-[180px] overflow-y-auto font-mono text-xs">
          <table className="w-full text-left border-collapse">
            <thead className="sticky top-0 bg-zinc-900 text-gray-400 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="p-2 border-b border-white/10 w-1/3">Log. System</th>
                <th className="p-2 border-b border-white/10 w-1/2">Name</th>
                <th className="p-2 border-b border-white/10 text-center w-12">Acción</th>
              </tr>
            </thead>
            <tbody className="text-gray-300 divider-y divide-white/5">
              {systems.map((sys, idx) => {
                const isTarget = sys.logSystem === 'ARIBACIG';
                return (
                  <tr 
                    key={idx} 
                    className={`border-b border-white/5 transition-all ${
                      isTarget 
                        ? 'bg-blue-500/10 text-white font-semibold border-l-2 border-l-blue-500' 
                        : 'hover:bg-white/5'
                    }`}
                  >
                    <td className={`p-2 uppercase ${isTarget ? 'text-blue-400 font-bold' : 'text-gray-400'}`}>
                      {sys.logSystem}
                    </td>
                    <td className={`p-2 truncate ${isTarget ? 'text-yellow-200' : 'text-gray-300'}`}>
                      {sys.name}
                    </td>
                    <td className="p-1 text-center">
                      <button
                        type="button"
                        onClick={() => setSystems(systems.filter(s => s.logSystem !== sys.logSystem))}
                        disabled={isTarget}
                        className={`p-1 rounded transition-all ${
                          isTarget ? 'text-gray-600 cursor-not-allowed' : 'text-gray-500 hover:text-red-400 hover:bg-white/5'
                        }`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* 📋 CAMPOS FORMULARIO TÉCNICO (Mantenemos tus credenciales originales de API ocultas de forma segura) */}
      <div className="bg-white/5 p-4 rounded-xl border border-white/5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Confirmar SAP Ariba Realm ID <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={realmId}
            onChange={(e) => onChange('realmId', e.target.value)}
            placeholder="ej. miempresa-T"
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm focus:outline-none text-white ${
              isInvalid && !realmId ? 'border-red-500' : 'border-white/10'
            }`}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-400 mb-1">
            Application Client ID (OAuth) <span className="text-red-400">*</span>
          </label>
          <input
            type="password"
            value={apiClientId}
            onChange={(e) => onChange('apiClientId', e.target.value)}
            placeholder="••••••••••••••••••••••••"
            className={`w-full bg-zinc-800 border rounded-lg p-2.5 text-sm focus:outline-none text-white ${
              isInvalid && !apiClientId ? 'border-red-500' : 'border-white/10'
            }`}
          />
        </div>
      </div>
    </div>
  );
}